import { useEffect, useRef, useState } from 'react'
import { ClipboardPaste } from 'lucide-react'
import { Button } from '@/components/ds2'
import { log } from 'utils/logger'
import { trackBoxTruckUiInteraction } from '../utils/analytics'

interface DesignPasteButtonProps {
    label: string
    /** Receives the clipboard as plain text and, when the browser offers it, as HTML. Returns whether it was used. */
    onPaste: (plainText: string, html: string) => boolean
    variant?: 'primary' | 'secondary'
}

/**
 * Pastes from the clipboard in one click. A browser may refuse to let the page read the clipboard (some do
 * by default, or the user denied it); then it asks for the paste shortcut instead, which always works.
 */
export const DesignPasteButton = ({ label, onPaste, variant = 'secondary' }: DesignPasteButtonProps) => {
    const [askForShortcut, setAskForShortcut] = useState(false)
    const pasteTargetRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (askForShortcut) pasteTargetRef.current?.focus()
    }, [askForShortcut])

    const readClipboard = async () => {
        let text: string
        try {
            text = await navigator.clipboard.readText()
        } catch (error) {
            // Not a failure the operator can fix here — the Ctrl+V fallback below always works — but a browser
            // or permission change that breaks one-click paste for everyone must show up somewhere.
            log.w('Clipboard read refused, asking for the paste shortcut instead', { error })
            trackBoxTruckUiInteraction('template_clipboard_read_blocked', error instanceof DOMException ? error.name : 'unknown')
            setAskForShortcut(true)
            return
        }
        onPaste(text, '')
    }

    if (askForShortcut) {
        return (
            <div
                ref={pasteTargetRef}
                tabIndex={0}
                role="textbox"
                aria-label="Paste here"
                onPaste={(event) => {
                    event.preventDefault()
                    setAskForShortcut(false)
                    onPaste(event.clipboardData.getData('text/plain'), event.clipboardData.getData('text/html'))
                }}
                className="flex flex-wrap items-center gap-2 rounded-xl border-2 border-dashed border-accent px-3 py-2 text-sm text-foreground outline-none"
            >
                <span>
                    Now press <strong>Ctrl+V</strong> (<strong>⌘V</strong> on Mac) to paste.
                </span>
                <Button variant="ghost" size="sm" onClick={() => setAskForShortcut(false)}>
                    Cancel
                </Button>
            </div>
        )
    }

    return (
        <Button variant={variant} size="sm" prefix={<ClipboardPaste size={16} />} onClick={() => void readClipboard()}>
            {label}
        </Button>
    )
}
